import {ChartModel} from '@xh/hoist/cmp/chart';
import {HoistModel, LoadSpec, lookup, managed, PlainObject, XH} from '@xh/hoist/core';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {action, observable} from '@xh/hoist/mobx';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {EconomicIndicator, EconomicIndicatorData, EconomicIndicatorOptions} from '../../../../data/DataTypes';
import {fmtNumber, fmtPercent, fmtThousands} from '@xh/hoist/format';

export class EcoIndicatorWidgetModel extends HoistModel {
    @observable accessor indicator: string;
    private indicatorOptions: EconomicIndicatorOptions;
    private TRILLION = 1000 * 1000 * 1000 * 1000;
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @managed chartModel = new ChartModel({highchartsConfig: this.getChart()});

    override onLinked() {
        this.markPersist('indicator', {dashViewModel: this.dashViewModel});

        this.dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            items: this.getIndicators().map(ind => ({
                text: ind.label,
                key: ind.value,
                actionFn: () => this.setIndicator(ind)
            }))
        });
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        if (!this.indicator) return;
        if (!this.indicatorOptions) {
            this.indicatorOptions = this.getIndicators().find(ind => ind.value == this.indicator).options;
        }

        this.chartModel.clear();

        const data: EconomicIndicatorData[] = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/economics/indicators/${this.indicator}`,
            method: 'GET',
            ...getDefaultOptions()
        });

        this.chartModel.setSeries({
            type: 'line',
            data: data.map(d => ({
                x: Date.parse(d.date),
                y: d.value * (this.indicatorOptions.scaleFactor ?? 1)
            }))
        });
    }

    @action
    private setIndicator(indicator: EconomicIndicator) {
        this.indicator = indicator.value;
        this.indicatorOptions = this.getIndicators().find(ind => ind.value == this.indicator).options;
        this.dashViewModel.title = indicator.label;
        this.loadAsync();
    }

    private getIndicators(): EconomicIndicator[] {
        return [
            {value: 'GDP', label: 'GDP', options: {units: 'T', scaleFactor: 1000000000}},
            {value: 'realGDP', label: 'Real GDP', options: {units: 'T', scaleFactor: 1000000000}},
            {
                value: 'nominalPotentialGDP',
                label: 'Nominal Potential GDP',
                options: {units: 'T', scaleFactor: 1000000000}
            },
            {value: 'realGDPPerCapita', label: 'Real GDP Per Capita', options: {units: 'K'}},
            {value: 'CPI', label: 'CPI', options: {units: null}},
            {
                value: 'inflationRate',
                label: 'Inflation Rate',
                options: {units: '%', scaleFactor: 0.01}
            },
            {
                value: 'unemploymentRate',
                label: 'Unemployment Rate',
                options: {units: '%', scaleFactor: 0.01}
            },
            {
                value: '30YearFixedRateMortgageAverage',
                label: '30Y Mortgages',
                options: {units: '%', scaleFactor: 0.01}
            }
        ];
    }

    private formatValue(value: number) {
        if (this.indicatorOptions.units == '%') return fmtPercent(value, {precision: 2});
        else if (this.indicatorOptions.units == 'K') return fmtThousands(value, {precision: 0, prefix: '$', label: true, asHtml: true});
        else if (this.indicatorOptions.units == 'T') return fmtNumber(value / this.TRILLION, {precision: 0, prefix: '$', asHtml: true}) + 'T';
        else return fmtNumber(value, {precision: 0});
    }

    private getChart(): PlainObject {
        const model = this;
        return {
            chart: {
                type: 'line',
                animation: false
            },
            xAxis: {
                type: 'datetime'
            },
            yAxis: {
                title: {
                    enabled: false
                },
                labels: {
                    formatter: function () {
                        return model.formatValue(this.value);
                    }
                }
            },
            plotOptions: {
                series: {animation: false}
            },
            legend: {enabled: false},
            tooltip: {
                shared: true,
                xDateFormat: '%Y-%m-%d',
                pointFormatter: function () {
                    return `<b>${model.formatValue(this.y)}</b>`;
                }
            }
        };
    }
}
